
import React, { useState } from 'react';
import { MemberModel, BranchModel, UserRole, UserStatus } from '../../../../models';
import { Search, Shield, Building2, Calendar, Phone, MoreVertical, Edit2, Trash2, CheckCircle2, XCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '../../../../firebase';
import AdminPageHeader from '../../frame/RightPanelHeader';

interface MemberManagementProps {
    members: MemberModel[];
    onUpdateMember: (member: MemberModel) => void;
}

const MemberManagement: React.FC<MemberManagementProps> = ({ members, onUpdateMember }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<'all' | UserRole>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // Edit Modal State
    const [editingMember, setEditingMember] = useState<MemberModel | null>(null);

    // Presence State
    const [presence, setPresence] = useState<Record<string, { state: string; last_changed: number }>>({});

    React.useEffect(() => {
        const statusRef = ref(rtdb, 'status');
        const unsubscribe = onValue(statusRef, (snapshot) => {
            if (snapshot.exists()) {
                setPresence(snapshot.val());
            }
        });
        return () => unsubscribe();
    }, []);

    const filteredMembers = members.filter(m => {
        const matchesSearch = m.name.includes(searchTerm) || m.phone?.includes(searchTerm) || m.email.includes(searchTerm);
        const matchesRole = filterRole === 'all' || m.role === filterRole;
        // Show only Manager and Staff for this view? Or all? 
        // Plan said "View all users", but primary focus is Manager/Staff. 
        // Let's look at all so we can promote Students.
        return matchesSearch && matchesRole;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filteredMembers.length / ITEMS_PER_PAGE);
    const paginatedMembers = filteredMembers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Reset page to 1 when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterRole]);

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingMember) return;
        onUpdateMember(editingMember);
        setEditingMember(null);
    };

    return (
        <div className="h-full flex flex-col bg-slate-50">
            {/* Header & Filters */}
            <AdminPageHeader
                title="멤버 및 권한 관리"
                subtitle="전체 사용자의 역할, 지점, 접근 권한을 관리합니다."
                icon={Shield}
                actions={
                    <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="이름, 연락처 검색"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-2 bg-slate-50 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20"
                            />
                        </div>
                        <select
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value as any)}
                            className="py-2 px-3 bg-slate-50 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                            <option value="all">모든 역할</option>
                            <option value="super_admin">최고 관리자</option>
                            <option value="teacher">선생님</option>
                            <option value="student">학생</option>
                            <option value="parent">학부모</option>
                        </select>
                    </div>
                }
            />

            {/* Table */}
            <div className="flex-1 px-8 pt-8 pb-8 overflow-hidden flex flex-col">
                <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="overflow-x-auto custom-scrollbar flex-1">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50">
                                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider">이름 / 연락처</th>
                                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider">역할</th>
                                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider">상태</th>
                                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider">접근 만료일</th>
                                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right">관리</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {paginatedMembers.map(member => (
                                    <tr key={member.id} className="hover:bg-slate-50/80 transition-colors group">
                                        <td className="p-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black ${member.role === 'super_admin' ? 'bg-rose-50 text-rose-600' :
                                                    member.role === 'teacher' ? 'bg-indigo-50 text-indigo-600' :
                                                        member.role === 'parent' ? 'bg-amber-50 text-amber-600' :
                                                            'bg-slate-50 text-slate-600'
                                                    }`}>
                                                    {member.name[0]}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-800 text-sm">{member.name}</p>
                                                    <p className="text-xs text-slate-400 font-medium">{member.phone}</p>
                                                    <p className="text-xs text-slate-400 font-medium">{member.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-5">
                                            <span className={`px-2.5 py-1 rounded-lg text-[11px] font-black border ${member.role === 'super_admin' ? 'bg-rose-50 text-rose-600 border-rose-100' :
                                                member.role === 'teacher' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' :
                                                    member.role === 'parent' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                                                        'bg-slate-50 text-slate-500 border-slate-100'
                                                }`}>
                                                {member.role.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="p-5">
                                            {member.status === 'suspended' ? (
                                                <span className="flex items-center gap-1.5 text-rose-500 font-bold text-xs">
                                                    <XCircle size={14} /> 정지됨
                                                </span>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <span className="flex items-center gap-1.5 text-emerald-500 font-bold text-xs">
                                                        <CheckCircle2 size={14} /> 활동중
                                                    </span>
                                                    {presence[member.id]?.state === 'online' && (
                                                        <span className="relative flex h-2 w-2">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-5">
                                            {member.role === 'teacher' && member.accessPolicy?.dueAt ? (
                                                <div className="flex items-center gap-1.5 text-slate-600 font-bold text-xs">
                                                    <Calendar size={14} className="text-slate-300" />
                                                    {new Date(member.accessPolicy.dueAt.toMillis ? member.accessPolicy.dueAt.toMillis() : member.accessPolicy.dueAt).toLocaleDateString()}
                                                </div>
                                            ) : (
                                                <span className="text-slate-300 text-xs">-</span>
                                            )}
                                        </td>
                                        <td className="p-5 text-right">
                                            <button
                                                onClick={() => setEditingMember(member)}
                                                className="p-2 hover:bg-white hover:shadow-md rounded-xl transition-all text-slate-400 hover:text-indigo-600"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 0 && (
                        <div className="p-4 border-t border-slate-100 flex items-center justify-center gap-2">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                                <ChevronsLeft size={18} />
                            </button>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                                <ChevronLeft size={18} />
                            </button>

                            <div className="flex items-center gap-1 mx-2">
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || (p >= currentPage - 2 && p <= currentPage + 2))
                                    .map((page, index, array) => (
                                        <React.Fragment key={page}>
                                            {index > 0 && array[index - 1] !== page - 1 && (
                                                <span className="text-slate-300 px-1">...</span>
                                            )}
                                            <button
                                                onClick={() => setCurrentPage(page)}
                                                className={`w-8 h-8 rounded-lg text-xs font-black transition-all ${currentPage === page
                                                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                                                    : 'text-slate-500 hover:bg-slate-100'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        </React.Fragment>
                                    ))}
                            </div>

                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                                <ChevronRight size={18} />
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                                <ChevronsRight size={18} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            {
                editingMember && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                        <form onSubmit={handleSave} className="bg-white w-full max-w-lg rounded-2xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-16 h-16 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <Shield size={32} />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-800">권한 및 정보 수정</h3>
                                    <p className="text-slate-400 font-bold text-sm">대상: {editingMember.name}</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest pl-1">역할 (Role)</label>
                                        <select
                                            value={editingMember.role}
                                            onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value as UserRole })}
                                            className="w-full p-4 bg-slate-50 rounded-xl border border-slate-100 font-bold text-slate-700 outline-none focus:border-indigo-500"
                                        >
                                            <option value="super_admin">Super Admin</option>
                                            <option value="teacher">Teacher</option>
                                            <option value="student">Student</option>
                                            <option value="parent">Parent</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest pl-1">상태 (Status)</label>
                                        <select
                                            value={editingMember.status}
                                            onChange={(e) => setEditingMember({ ...editingMember, status: e.target.value as UserStatus })}
                                            className="w-full p-4 bg-slate-50 rounded-xl border border-slate-100 font-bold text-slate-700 outline-none focus:border-indigo-500"
                                        >
                                            <option value="active">Active (활동)</option>
                                            <option value="suspended">Suspended (정지)</option>
                                        </select>
                                    </div>
                                </div>

                                {editingMember.role === 'teacher' && (
                                    <div className="space-y-2">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest pl-1">접근 만료일 (Access Until)</label>
                                        <input
                                            type="date"
                                            value={editingMember.accessPolicy?.dueAt ? new Date(editingMember.accessPolicy.dueAt.toMillis ? editingMember.accessPolicy.dueAt.toMillis() : editingMember.accessPolicy.dueAt).toISOString().split('T')[0] : ''}
                                            onChange={(e) => {
                                                const date = new Date(e.target.value);
                                                setEditingMember({
                                                    ...editingMember,
                                                    accessPolicy: {
                                                        type: 'PERIOD',
                                                        dueAt: Timestamp.fromDate(date) // Assuming Timestamp import or handling
                                                    }
                                                })
                                            }}
                                            className="w-full p-4 bg-slate-50 rounded-xl border border-slate-100 font-bold text-slate-700 outline-none focus:border-indigo-500"
                                        />
                                        <p className="text-[11px] text-slate-400 font-medium pl-1">* 만료일이 지나면 계정 접근이 차단됩니다.</p>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 mt-10">
                                <button
                                    type="button"
                                    onClick={() => setEditingMember(null)}
                                    className="flex-1 py-4 rounded-xl border border-slate-200 font-bold text-slate-500 hover:bg-slate-50"
                                >
                                    취소
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-4 rounded-xl bg-indigo-600 font-bold text-white hover:bg-indigo-700 shadow-xl shadow-indigo-200"
                                >
                                    변경사항 저장
                                </button>
                            </div>
                        </form>
                    </div>
                )
            }
        </div>
    );
};

export default MemberManagement;
